import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import { FaPen, FaPlus, FaTrash } from 'react-icons/fa';
import Preloader from './Preloader';
import { useMemo } from 'react';

function MissingReportsHub() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: '',
    lastseen: '',
    contact: '',
    dateMissing: '',
    details: '',
    image1_url: '',
    image2_url: '',
    image3_url: '',
    image4_url: '',
    category: 'person',
    status: 'missing',
    pet_details: '',
    pet_color: ''
  });
  const [imageFiles, setImageFiles] = useState([null, null, null, null]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all, missing, found
  const [reporterProfiles, setReporterProfiles] = useState({});
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [contactError, setContactError] = useState('');
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    document.title = "Missing Reports";
  }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchReports();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      // Real-time updates for missing reports
      const missingReportsChannel = supabase
        .channel('public:missing_reports_hub')
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'missing_reports' },
          (payload) => {
            console.log('Missing report change received!', payload);
            fetchReports();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(missingReportsChannel);
      };
    }
  }, [currentUser]);

  async function fetchReports() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('missing_reports')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      setError('Failed to load reports');
      setReports([]);
    } else {
      setReports(data || []);
      // Fetch reporter profiles
      const userIds = Array.from(new Set((data || []).map(r => r.user_id).filter(Boolean)));
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id,first_name,last_name')
          .in('user_id', userIds);
        const map = {};
        (profiles || []).forEach(p => {
          map[p.user_id] = p;
        });
        setReporterProfiles(map);
      } else {
        setReporterProfiles({});
      }
    }
    setLoading(false);
  }

  function getReporterName(user_id) {
    const profile = reporterProfiles[user_id];
    if (!profile) return 'Unknown';
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  }

  async function handleImageUpload(event, index) {
    const file = event.target.files[0];
    if (file) {
      try {
        const newImageFiles = [...imageFiles];
        newImageFiles[index] = file;
        setImageFiles(newImageFiles);

        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('missing-images')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Failed to upload image. Please try again.');
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('missing-images')
          .getPublicUrl(filePath);

        setForm(prev => ({
          ...prev,
          [`image${index + 1}_url`]: publicUrl
        }));

      } catch (error) {
        console.error('Error in handleImageUpload:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  function openEdit(report) {
    let formattedDate = '';
    if (report.dateMissing) {
      const dateObj = new Date(report.dateMissing);
      if (!isNaN(dateObj.getTime())) {
        formattedDate = dateObj.toISOString().slice(0, 16);
      }
    }
    setForm({
      name: report.name || '',
      age: report.age || '',
      gender: report.gender || '',
      lastseen: report.lastseen || '',
      contact: report.contact || '',
      dateMissing: formattedDate,
      details: report.details || '',
      image1_url: report.image1_url || '',
      image2_url: report.image2_url || '',
      image3_url: report.image3_url || '',
      image4_url: report.image4_url || '',
      category: (report.category || report.cattegory || 'person').toLowerCase(),
      status: typeof report.status === 'string' ? report.status.toLowerCase() : 'missing',
      pet_details: report.pet_details || '',
      pet_color: report.pet_color || ''
    });
    setImageFiles([null, null, null, null]);
    setEditId(report.id);
    setShowForm(true);
  }

  async function handleAddNew(e) {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setContactError('');

    // Validate contact number (must be '+63' followed by 10 digits, e.g., +639123456789)
    const contactPattern = /^\+63\d{10}$/;
    if (!contactPattern.test(form.contact)) {
      setContactError('Please enter a valid mobile number (+639123456789)');
      setFormLoading(false);
      return;
    }

    try {
      if (!currentUser) {
        setFormError('User not authenticated');
        setFormLoading(false);
        return;
      }

      const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

      const reportData = {
        name: form.name,
        age: parseInt(form.age) || null,
        gender: form.gender,
        lastseen: form.lastseen,
        contact: form.contact,
        dateMissing: form.dateMissing,
        details: form.category === 'person' ? form.details : null,
        image1_url: form.image1_url || null,
        image2_url: form.image2_url || null,
        image3_url: form.image3_url || null,
        image4_url: form.image4_url || null,
        category: form.category,
        status: capitalize(form.status || 'missing'),
        user_id: currentUser.user_id,
        pet_details: form.category === 'pet' ? form.pet_details : null,
        pet_color: form.category === 'pet' ? form.pet_color : null
      };

      if (editId) {
        const { error } = await supabase.from('missing_reports').update(reportData).eq('id', editId);
        if (error) throw error;
        // Audit log for edit
        const { error: auditError } = await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edited missing report',
          target_id: editId,
          target_type: 'MissingReports',
          details: {
            name: reportData.name,
            category: reportData.category,
            status: reportData.status,
            contact: reportData.contact,
            lastseen: reportData.lastseen
          }
        }]);
      } else {
        const { data: insertData, error } = await supabase.from('missing_reports').insert([reportData]).select();
        if (error) throw error;
        // Audit log for create
        const newId = insertData && insertData.length > 0 ? insertData[0].id : null;
        const { error: auditError } = await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'created missing report',
          target_id: newId,
          target_type: 'MissingReports',
          details: {
            name: reportData.name,
            category: reportData.category,
            status: reportData.status,
            contact: reportData.contact,
            lastseen: reportData.lastseen
          }
        }]);
      }

      setShowForm(false);
      setForm({
        name: '', age: '', gender: '', lastseen: '', contact: '', dateMissing: '', details: '',
        image1_url: '', image2_url: '', image3_url: '', image4_url: '', category: 'person'
      });
      setImageFiles([null, null, null, null]);
      setEditId(null);
      fetchReports();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  }

  const handleCheckboxChange = (id) => {
    setSelectedReports(prev => {
      if (prev.includes(id)) {
        return prev.filter(reportId => reportId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      setSelectedReports(reports.map(report => report.id));
    } else {
      setSelectedReports([]);
    }
  };

  const handleArchiveSelected = async () => {
    if (selectedReports.length === 0) {
      alert('Please select at least one report to archive');
      return;
    }

    try {
      // Get reports for audit log
      const { data: reportsToArchive } = await supabase
        .from('missing_reports')
        .select('*')
        .in('id', selectedReports);

      const { error } = await supabase
        .from('missing_reports')
        .update({ status: 'Archived' })
        .in('id', selectedReports);

      if (error) throw error;

      // Audit log for each archived report
      if (reportsToArchive && reportsToArchive.length > 0) {
        for (const report of reportsToArchive) {
          const { error: auditError } = await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'archived missing report',
            target_id: report.id,
            target_type: 'MissingReports',
            details: {
              name: report.name,
              category: report.category,
              status: 'Archived',
              contact: report.contact,
              lastseen: report.lastseen
            }
          }]);
        }
      }
      setSelectedReports([]);
      fetchReports();
    } catch (err) {
      console.error('Error archiving reports:', err);
      alert('Failed to archive reports');
    }
  };

  // Filtered reports
  const filteredReports = reports.filter(report => {
    const matchesSearch =
      search.trim() === '' ||
      (report.name && report.name.toLowerCase().includes(search.toLowerCase())) ||
      (report.lastseen && report.lastseen.toLowerCase().includes(search.toLowerCase())) ||
      (report.contact && report.contact.toLowerCase().includes(search.toLowerCase())) ||
      (report.details && report.details.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory =
      categoryFilter === 'all' ||
      (report.category || report.cattegory) === categoryFilter;
    const normalizedStatus = typeof report.status === 'string' ? report.status.toLowerCase() : '';
    const matchesStatus =
      statusFilter === 'all' ||
      normalizedStatus === statusFilter; // 'missing' or 'found'

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (loading) return <Preloader text="Loading Reports..." />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8',}}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Missing Person and Pets Hub</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div className="search-actions-row">
          <div className="search-filters">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </span>
            </div>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="type-filter">
              <option value="all">All</option>
              <option value="person">Person</option>
              <option value="pet">Pet</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ flex: 1, minWidth: 140, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}>
              <option value="all">All Status</option>
              <option value="missing">Missing</option>
              <option value="found">Found</option>
            </select>
          </div>
          {currentUser && (
            <div className="action-buttons">
              <button onClick={() => setShowForm(true)} className="create-button">
                <FaPlus /> Add New
              </button>
              {(currentUser.role === 'admin' || currentUser.role === 'super admin') && (
                <button 
                  onClick={handleArchiveSelected}
                  disabled={selectedReports.length === 0}
                  className="archive-button"
                >
                  <FaTrash/> Archive Selected
                </button>
              )}
            </div>
          )}
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', minHeight: 200, overflowX: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#d32f2f' }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAllClick}
                      checked={selectedReports.length === reports.length && reports.length > 0}
                    />
                  </th>
                  <th style={{ padding: 12 }}>Image</th>
                  <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Name</th>
                  <th style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Address</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Reported by</th>
                  <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Contact</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.length > 0 ? paginatedReports.map(report => (
                  <tr key={report.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => navigate(`/missing-reports/${report.id}`)}>
                    <td style={{ padding: 12, position: 'relative', zIndex: 2 }}>
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(report.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'relative', zIndex: 2 }}
                      />
                    </td>
                    <td style={{ padding: 12 }}>
                      <img src={report.image1_url || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'} alt="profile" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', background: '#eee' }} />
                    </td>
                    <td style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{report.name}</td>
                    <td style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{report.lastseen}</td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background:
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'missing' ? '#ffebee' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'found' ? '#e6f4ea' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'archived' ? '#f5f5f5' :
                            '#f0f0f0',
                          color:
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'missing' ? '#c62828' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'found' ? '#19c37d' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'archived' ? '#616161' :
                            '#888',
                          border:
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'missing' ? '1px solid #c6282833' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'found' ? '1px solid #19c37d33' :
                            (typeof report.status === 'string' ? report.status.toLowerCase() : '') === 'archived' ? '1px solid #bdbdbd' :
                            '1px solid #ccc'
                        }}
                      >
                        {typeof report.status === 'string' ? report.status.charAt(0).toUpperCase() + report.status.slice(1) : 'Missing'}
                      </span>
                    </td>
                    <td style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8, height: 56, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <img src="https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg" alt="reporter" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                      <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{getReporterName(report.user_id)}</span>
                    </td>
                    <td style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{report.contact}</td>
                    <td style={{ padding: 12 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin' || (currentUser.role === 'barangay' && report.user_id === currentUser.user_id))) {
                            openEdit(report);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: 20,
                          cursor: (currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin' || (currentUser.role === 'barangay' && report.user_id === currentUser.user_id))) ? 'pointer' : 'not-allowed',
                          color: (currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin' || (currentUser.role === 'barangay' && report.user_id === currentUser.user_id))) ? '#333' : '#ccc'
                        }}
                      >
                        <FaPen />
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: 40, color: '#888' }}>No reports found</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
          <div style={{ display: 'flex', gap: 4 }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  background: page === i + 1 ? '#08b1c7' : '#fff',
                  color: page === i + 1 ? '#fff' : '#333',
                  fontWeight: page === i + 1 ? 'bold' : 'normal'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
        </div>
        {showForm && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              minWidth: 0,
              maxWidth: 600,
              width: '100%',
              height: 600,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)'
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexShrink: 0 }}>
                <h2 style={{ margin: 0, fontSize: 24 }}>
                  {editId ? 'Edit' : 'Add'} Missing Report
                </h2>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888',
                    marginLeft: 8
                  }}
                >
                  ×
                </button>
              </div>
              {/* Scrollable Form Fields */}
              <form
                onSubmit={handleAddNew}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Category *</label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  >
                    <option value="person">Person</option>
                    <option value="pet">Pet</option>
                  </select>
                </div>
                {form.category === 'person' || form.category === 'pet' ? (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Name *</label>
                      <input 
                        type="text" 
                        placeholder={form.category === 'person' ? "Enter the name of the missing person" : "Enter the name of the missing pet"}
                        value={form.name} 
                        onChange={e => setForm({ ...form, name: e.target.value })} 
                        required 
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} 
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>{form.category === 'person' ? 'Age' : 'Pet Age'}</label>
                      <input 
                        type="number" 
                        placeholder={form.category === 'person' ? "Enter age (optional)" : "Enter pet age (optional)"}
                        value={form.age} 
                        onChange={e => setForm({ ...form, age: e.target.value })} 
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} 
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>{form.category === 'person' ? 'Gender' : 'Pet Gender'}</label>
                      <select 
                        value={form.gender} 
                        onChange={e => setForm({ ...form, gender: e.target.value })} 
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      >
                        <option value="">Select {form.category === 'person' ? 'Gender' : 'Pet Gender'}</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Last Seen Location *</label>
                      <input 
                        type="text" 
                        placeholder={form.category === 'person' ? "Enter the last known location" : "Enter the last known location of the pet"}
                        value={form.lastseen} 
                        onChange={e => setForm({ ...form, lastseen: e.target.value })} 
                        required 
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} 
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Contact Number *</label>
                      <input
                        type="text"
                        placeholder="+639123456789"
                        value={form.contact}
                        onChange={e => {
                          let val = e.target.value;
                          if (!val.startsWith('+63')) {
                            val = '+63' + val.replace(/[^0-9]/g, '');
                          } else {
                            val = '+63' + val.slice(3).replace(/[^0-9]/g, '');
                          }
                          val = val.slice(0, 13);
                          setForm({ ...form, contact: val });
                          setContactError('');
                        }}
                        required
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      />
                      {contactError && <div style={{ color: '#d32f2f', marginTop: 4 }}>{contactError}</div>}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Date & Time Missing *</label>
                      <input
                        type="datetime-local"
                        value={form.dateMissing}
                        onChange={e => setForm({ ...form, dateMissing: e.target.value })}
                        required
                        style={{
                          padding: 12,
                          borderRadius: 8,
                          border: '1.5px solid #19c37d',
                          width: '100%',
                          maxWidth: '100%',
                          minWidth: 0,
                          fontSize: 16,
                          background: '#f8fefb',
                          boxShadow: '0 1px 4px #19c37d22',
                          boxSizing: 'border-box'
                        }}
                      />
                      <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                        Please select the exact date and time the {form.category === 'person' ? 'person' : 'pet'} was last seen.
                      </small>
                    </div>
                    {form.category === 'pet' && (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <label style={{ display: 'block', marginBottom: 8 }}>Pet Color *</label>
                          <input
                            type="text"
                            placeholder="Enter the color of the missing pet"
                            value={form.pet_color}
                            onChange={e => setForm({ ...form, pet_color: e.target.value })}
                            required
                            style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                          />
                        </div>
                      </>
                    )}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>{form.category === 'person' ? 'Details' : 'Pet Details'}{form.category === 'pet' && ' *'}</label>
                      <textarea 
                        placeholder={form.category === 'person' ? "Enter additional details about the missing person" : "Enter details about the missing pet"}
                        value={form.category === 'person' ? form.details : form.pet_details}
                        onChange={e => setForm({ ...form, [form.category === 'person' ? 'details' : 'pet_details']: e.target.value })}
                        required={form.category === 'pet'}
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', minHeight: 80, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} 
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
                      <select
                        value={typeof form.status === 'string' ? form.status.toLowerCase() : 'missing'}
                        onChange={e => setForm({ ...form, status: e.target.value })}
                        required
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      >
                        <option value="missing">Missing</option>
                        <option value="found">Found</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      {[1,2,3,4].some(i => !form[`image${i}_url`]) && (
                        <div style={{ marginBottom: 12}}>
                          <label
                            htmlFor="missing-image-upload"
                            style={{
                              display: 'inline-block',
                              padding: '10px 20px',
                              background: '#fff',
                              color: '#19c37d',
                              border: '1px solid #19c37d',
                              borderRadius: 8,
                              cursor: 'pointer'
                            }}
                          >
                            Upload Images (up to 4)
                          </label>
                          <input
                            id="missing-image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={e => {
                              const files = Array.from(e.target.files).slice(0, 4);
                              files.forEach((file, idx) => handleImageUpload({ target: { files: [file] } }, idx));
                            }}
                            style={{ display: 'none' }}
                          />
                        </div>
                      )}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 16,
                          position: 'relative',
                          maxWidth: 420,
                          margin: '0 auto'
                        }}
                      >
                        {Array.from({ length: 4 }).map((_, index) =>
                          form[`image${index + 1}_url`] ? (
                            <div key={index} style={{ position: 'relative' }}>
                              <img
                                src={form[`image${index + 1}_url`]}
                                alt={`Preview ${index + 1}`}
                                style={{ width: '100%', height: 150, objectFit: 'cover', marginTop: 0, borderRadius: 4 }}
                              />
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Pet-specific fields */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Pet Details *</label>
                      <textarea
                        placeholder="Enter details about the missing pet"
                        value={form.pet_details}
                        onChange={e => setForm({ ...form, pet_details: e.target.value })}
                        required
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', minHeight: 80, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8 }}>Pet Color *</label>
                      <input
                        type="text"
                        placeholder="Enter the color of the missing pet"
                        value={form.pet_color}
                        onChange={e => setForm({ ...form, pet_color: e.target.value })}
                        required
                        style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      />
                    </div>
                  </>
                )}
                {formError && (
                  <div style={{ color: '#d32f2f', marginBottom: 12 }}>{formError}</div>
                )}
              </form>
              {/* Modal Footer (fixed at bottom, outside scrollable area) */}
              <div style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'flex-end',
                marginTop: 24,
                flexShrink: 0,
                background: '#fff'
              }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 16
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="missing-report-form"
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#19c37d',
                    color: '#fff',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    fontSize: 16,
                    fontWeight: 'bold',
                    opacity: formLoading ? 0.7 : 1
                  }}
                  disabled={formLoading}
                  onClick={() => document.querySelector('form').requestSubmit()}
                >
                  {formLoading ? (editId ? 'Saving...' : 'Submitting...') : (editId ? 'Save Changes' : 'Submit')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MissingReportsHub;
